import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {IPlatformHomeMagicBar} from '@shared/components/platform-tool/platform-tool-magic-bar/models/magic-bar-models';
import {HomeSideBarItem} from '@shared/models/platform-tool-models';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {filter, take, takeUntil, timeout} from 'rxjs/operators';
import {
  DiagnosticReportMismatchedRecordCounterPartModel,
  DiagnosticReportMismatchedRecordSummaryModel,
  DiagnosticReportModel,
  DiagnosticReportSelectedRecordInfo,
  DiagnosticReportWorksheetModelDto
} from '@features/global-tax-calculator/models/diagnostic-report.model';
import {DiagnosticReportService} from '@features/global-tax-calculator/services/diagnostic-report.service';
import {FlexSheetService} from '@features/spreadsheet/services/flex-sheet.service';
import {Subject} from 'rxjs';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {CommandService} from '@core/services/command.service';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {BehaviorSubject} from 'rxjs';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {interval, timer} from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-diagnostic-report-container',
  templateUrl: './diagnostic-report-container.component.html',
  styleUrl: './diagnostic-report-container.component.scss'
})
export class DiagnosticReportContainerComponent  implements OnInit, OnDestroy, IPlatformHomeMagicBar   {
  data: any;
  homeSideBarItem: HomeSideBarItem;
  openDiagnosticReport = true;
  diagnosticReportWorksheetModels: Array<DiagnosticReportWorksheetModelDto> = new Array<DiagnosticReportWorksheetModelDto>();
  selectedMismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel;
  unSubscribe$: Subject<void> = new Subject<void>();
  diagnosticReportSelectedCellInfo: DiagnosticReportSelectedRecordInfo = new DiagnosticReportSelectedRecordInfo();
  diagnosticReport: DiagnosticReportModel;
  selectedFieldName: string;
  hideDiagnosticReportDescription$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private platformQueryService: PlatformQueryService,
              private diagnosticReportService: DiagnosticReportService,
              private commandService: CommandService,
              private flexSheetService: FlexSheetService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService) {
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {
    this.fetchDiagnosticReportsData();
    this.diagnosticReportService.getDiagnosticReportActionObservable().pipe(takeUntil(this.unSubscribe$)).subscribe(action => {
      this.handleAction(action);
    });
  }

  private fetchDiagnosticReportsData() {
    if (this.homeSideBarItem.query) {
      this.platformQueryService.executeQuery(this.homeSideBarItem.query).pipe(take(1)).subscribe(response  => {
        if (response && response.fieldValues) {
          this.diagnosticReportWorksheetModels = response.fieldValues['DiagnosticReportWorksheetModelDtoModels'];
          this.diagnosticReport = response.fieldValues['DiagnosticReportModel'];
          this.diagnosticReportSelectedCellInfo = new DiagnosticReportSelectedRecordInfo();
          this.hideDiagnosticReportDescription$.next(this.diagnosticReport.isDescriptionHidden);
        }
      });
    }
  }

  handleAction(action: any): void {
    switch (action.actionType) {
      case 'ShowDiagnosticReportCellRecordMismatchedHistory':
        this.showDiagnosticReportCellRecordMismatchedHistory(action);
        break;
    }
  }

  onHideDiagnosticReportDescription($event: boolean) {
    const hideDiagnosticReportDescriptionCommand = new PlatformCommand('HideDiagnosticReportDescriptionCommand', Api.Spreadsheet);
    hideDiagnosticReportDescriptionCommand.setValue('id', this.diagnosticReport.id);
    this.commandService.executePlatformCommand(hideDiagnosticReportDescriptionCommand).pipe(take(1)).subscribe(response => {
      if(response && response.fieldValues && response.fieldValues['Hide']) {
        this.diagnosticReport.isDescriptionHidden = response.fieldValues['Hide'];
        this.hideDiagnosticReportDescription$.next(this.diagnosticReport.isDescriptionHidden);
      }
    });
  }

  onSelectedMismatchedRecordEvent(selectedMismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel) {
    interval(300)
      .pipe(
        filter(() => this.isSelectedCellFound(selectedMismatchedRecordSummary)),
        take(1),
        timeout(2100)
      )
      .subscribe({
        next: () => {
          const selectedCell = this.flexSheetService.selection.cellModel;
          selectedMismatchedRecordSummary.comment = selectedCell.cmt;
          selectedMismatchedRecordSummary.cellValue = selectedCell.v;
          this.diagnosticReportSelectedCellInfo.selectedReportId = selectedMismatchedRecordSummary.id;
          this.diagnosticReportSelectedCellInfo.rowIndex = selectedMismatchedRecordSummary.rowIndex;
          this.diagnosticReportSelectedCellInfo.columnIndex = selectedMismatchedRecordSummary.columnIndex;
          this.diagnosticReportSelectedCellInfo.fieldModelId = selectedMismatchedRecordSummary.fieldModelId;
          this.selectedMismatchedRecordSummary = selectedMismatchedRecordSummary;
          this.selectedFieldName = selectedMismatchedRecordSummary.fieldName;
          this.openDiagnosticReport = false;
        },
        error: () => {
          this.snackBarService.showError('Cell not found.');
        }
      });
  }
  private isSelectedCellFound(mismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel): boolean {
    const selectedCell = this.flexSheetService.selection.cellModel;
    if (!selectedCell) {
      return false;
    }
    return selectedCell.er + 1 === mismatchedRecordSummary.rowIndex && selectedCell.ec + 1 === mismatchedRecordSummary.columnIndex;
  }

  onShow($event: any) {
    this.openDiagnosticReport = $event.openDiagnosticReport;
  }

  private showDiagnosticReportCellRecordMismatchedHistory(action: any) {
    const cellInfoData = action.data;
    const selectedCell = this.flexSheetService.selection.cellModel;
    if (!selectedCell || !selectedCell.cmt) {
      this.diagnosticReportSelectedCellInfo = new DiagnosticReportSelectedRecordInfo();
      this.selectedMismatchedRecordSummary = null;
      this.openDiagnosticReport = true;
    } else {
      const selectedRecordSummary = new DiagnosticReportMismatchedRecordSummaryModel();
      selectedRecordSummary.comment = selectedCell.cmt;
      selectedRecordSummary.cellValue = selectedCell.v;
      selectedRecordSummary.rowIndex = selectedCell.er + 1;
      selectedRecordSummary.columnIndex = selectedCell.ec + 1;
      selectedRecordSummary.fieldName = '';
      this.selectedMismatchedRecordSummary = selectedRecordSummary;

      this.diagnosticReportSelectedCellInfo.rowIndex = this.selectedMismatchedRecordSummary.rowIndex;
      this.diagnosticReportSelectedCellInfo.columnIndex = this.selectedMismatchedRecordSummary.columnIndex;
      this.diagnosticReportSelectedCellInfo.worksheetName = cellInfoData.WorksheetName;

      this.openDiagnosticReport = false;
    }
  }

  private GetDRWorksheetId(currentWorksheetName: string) {
    const dRWorksheetModel = this.diagnosticReportWorksheetModels.find(o => o.worksheetName === currentWorksheetName);
    if (dRWorksheetModel) {
      return dRWorksheetModel.id;
    }
    return '';
  }


  onNavigateToSource(counterPart: DiagnosticReportMismatchedRecordCounterPartModel) {
    const platformQuery = new PlatformQuery();
    platformQuery.name = 'GetDiagnosticReportNavigationInfoQuery';
    platformQuery.api = Api.Spreadsheet;
    platformQuery.setValue('ScenarioId', this.diagnosticReport.scenarioId);
    platformQuery.setValue('ScenarioType', this.diagnosticReport.scenarioType);
    platformQuery.setValue('CountryCode', counterPart.countryCode);
    platformQuery.setValue('Type', counterPart.type);
    if (counterPart.type === 'Qnr') {
      platformQuery.setValue('WorksheetName', this.flexSheetService.getCurrentWorksheetName());
      platformQuery.setValue('WorksheetId', this.flexSheetService.getCurrentWorksheetId());
    } else {
      platformQuery.setValue('WorksheetId', counterPart.fieldValues['WorksheetId']);
    }
    this.platformQueryService.executeQuery(platformQuery).pipe(takeUntil(this.unSubscribe$)).subscribe(res => {
      if (res.fieldValues['DirectoryEntryId']) {
        counterPart.fieldValues['DirectoryEntryId'] = res.fieldValues['DirectoryEntryId'];
      }
      if (res.fieldValues['WorkbookId']) {
        counterPart.fieldValues['WorkbookId'] = res.fieldValues['WorkbookId'];
      }
      switch (counterPart.type) {
        case 'Calculator':
          this.navigateToCalculator(counterPart);
          break;
        case 'Qnr':
          this.navigateToQnr(counterPart);
          break;
      }
    }, err => {
      console.error(err);
    });
  }

  private navigateToCalculator(counterPart: DiagnosticReportMismatchedRecordCounterPartModel) {
    const diagnosticReportOpenCalculatorCommand = new PlatformCommand('DiagnosticReportOpenCalculatorCommand', Api.Spreadsheet);
    diagnosticReportOpenCalculatorCommand.setValue('ScenarioId', this.diagnosticReport.scenarioId);
    diagnosticReportOpenCalculatorCommand.setValue('ScenarioType', this.diagnosticReport.scenarioType);
    diagnosticReportOpenCalculatorCommand.setValue('CountryCode', counterPart.countryCode);
    diagnosticReportOpenCalculatorCommand.setValue('DefaultWorksheetName', counterPart.fieldValues['WorksheetName']);
    diagnosticReportOpenCalculatorCommand.setValue('LoadWorkbook', counterPart.fieldValues['WorkbookId']);
    diagnosticReportOpenCalculatorCommand.setValue('LoadWorksheet', counterPart.fieldValues['WorksheetId']);
    diagnosticReportOpenCalculatorCommand.setValue('SelectedRowIndex', this.selectedMismatchedRecordSummary.rowIndex);
    diagnosticReportOpenCalculatorCommand.setValue('SelectedColumnIndex', this.selectedMismatchedRecordSummary.columnIndex);

    this.executeCommand(diagnosticReportOpenCalculatorCommand);
  }

  private executeCommand(command: PlatformCommand) {
    if (command.api && command.api.length > 0) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          if (commandResponse.fieldValues['RedirectCommand']) {
            this.executeCommand(commandResponse.fieldValues['RedirectCommand']);
            return;
          }
          if (commandResponse.fieldValues['RedirectUrl']) {
            const redirectUrl = commandResponse.fieldValues['RedirectUrl'];
            this.fuseSplashScreenService.hide();
            window.open(redirectUrl, '_blank', 'noreferrer').focus();
          }
          this.fuseSplashScreenService.hide();
        },
        error => {
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private navigateToQnr(counterPart: DiagnosticReportMismatchedRecordCounterPartModel) {
    const openQnrResponseCommand = new PlatformCommand('NavigateToQnrResponseCommand', Api.Spreadsheet);
    openQnrResponseCommand.setValue('ScenarioId', this.diagnosticReport.scenarioId);
    openQnrResponseCommand.setValue('ScenarioType', this.diagnosticReport.scenarioType);
    openQnrResponseCommand.setValue('CountryCode', counterPart.countryCode);
    openQnrResponseCommand.setValue('DirectoryEntryId', counterPart.fieldValues['DirectoryEntryId']);
    openQnrResponseCommand.setValue('DataItemId', counterPart.fieldValues['DataItemId']);
    openQnrResponseCommand.setValue('SelectedQuestionKey', counterPart.fieldValues['FieldKey']);
    openQnrResponseCommand.setValue('WorksheetId', this.flexSheetService.getCurrentWorksheetId());
    this.commandService.executePlatformCommand(openQnrResponseCommand).pipe(take(1)).subscribe(response => {
      if(response && response.fieldValues && response.fieldValues['RedirectUrl']) {
        const redirectUrl = response.fieldValues['RedirectUrl'];
        window.open(redirectUrl, '_blank', 'noreferrer').focus();
      }
    });
  }

  onSelectedWorksheetName(worksheetName: string) {
    if (worksheetName) {
      this.diagnosticReportSelectedCellInfo = new DiagnosticReportSelectedRecordInfo();
      this.diagnosticReportSelectedCellInfo.worksheetName = worksheetName;
    }
  }
}
